import { useState } from "react";
import { Copy, Gift, Search, Share2, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import { api, waLink } from "../../api";
import { WhatsAppIcon } from "../Logo";

export default function Referrals() {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [checkCode, setCheckCode] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const r = await api.post("/referrals", { owner_name: name, owner_contact: contact });
      setGenerated(r.data);
      toast.success("¡Código creado!");
    } catch (err) {
      toast.error("No se pudo crear el código. Revisa los datos e inténtalo de nuevo.");
    } finally {
      setGenerating(false);
    }
  };

  const check = async (e) => {
    e.preventDefault();
    if (!checkCode.trim()) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const r = await api.get(`/referrals/${encodeURIComponent(checkCode.trim())}`);
      setCheckResult(r.data);
    } catch {
      toast.error("Código no válido");
    } finally {
      setChecking(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(generated.code);
      toast.success("Código copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const shareMessage = generated
    ? `¡Mira NubeVape España! Vapers desechables originales al mejor precio. Usa mi código ${generated.code} al hacer tu pedido y te hacen un ${generated.buyer_pct}% de descuento: ${window.location.origin}`
    : "";

  return (
    <section id="referidos" data-testid="referrals-section" className="px-4 py-16 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#7C5CFF]">
            Plan amigo
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Recomienda y consigue pedidos{" "}
            <span className="text-gradient">GRATIS</span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-[#8A8F98] sm:text-base">
            Genera tu código de referido y compártelo: tus amigos consiguen un 10% de descuento y{" "}
            <span className="font-semibold text-[#F2F4F6]">
              tú acumulas un 10% por cada compra que hagan
            </span>
            . Con 10 amigos consigues el <span className="font-bold text-[#2EE6D6]">100% de descuento</span>{" "}
            (hasta 100€): <span className="font-bold text-[#2EE6D6]">un pedido de 100€ te sale GRATIS</span>.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-[#2D323E] bg-[#1E2128] p-6">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#2EE6D6]/15 to-[#7C5CFF]/15">
              <Gift className="h-5 w-5 text-[#2EE6D6]" />
            </div>
            <h3 className="font-heading text-lg font-semibold">Tu amigo se ahorra un 10%</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#8A8F98]">
              Quien haga un pedido con tu código obtiene un <span className="font-semibold text-[#F2F4F6]">10% de descuento</span> directo
              en el total (acumulable a los descuentos por cantidad).
            </p>
          </div>
          <div className="rounded-2xl border border-[#7C5CFF]/40 bg-[#7C5CFF]/10 p-6">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#2EE6D6]/15 to-[#7C5CFF]/15">
              <TicketPercent className="h-5 w-5 text-[#7C5CFF]" />
            </div>
            <h3 className="font-heading text-lg font-semibold">Tú acumulas un 10% por compra</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#8A8F98]">
              Por cada amigo que compre con tu código sumas un{" "}
              <span className="font-semibold text-[#F2F4F6]">10% de descuento acumulable</span>.
              Con 10 amigos llegas al <span className="font-semibold text-[#F2F4F6]">100%</span>{" "}
              (máximo <span className="font-semibold text-[#F2F4F6]">100€</span> de descuento): si
              tu pedido es de 100€, <span className="font-semibold text-[#2EE6D6]">te sale gratis</span>.
              Al comprar, dinos tu código por WhatsApp y te lo aplicamos sobre el precio final.
            </p>
          </div>
          <div className="rounded-2xl border border-[#2D323E] bg-[#1E2128] p-6">
            <h3 className="font-heading text-lg font-semibold">Genera tu código gratis</h3>
            {!generated ? (
              <form onSubmit={generate} className="mt-4 space-y-3" data-testid="referral-generate-form">
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="referral-name-input"
                  placeholder="Tu nombre"
                  className="w-full rounded-lg border border-[#2D323E] bg-[#16181D] px-3 py-2.5 text-sm text-[#F2F4F6] focus:border-[#2EE6D6] focus:outline-none"
                />
                <input
                  type="tel"
                  required
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  data-testid="referral-contact-input"
                  placeholder="Tu WhatsApp (ej: 612 345 678)"
                  className="w-full rounded-lg border border-[#2D323E] bg-[#16181D] px-3 py-2.5 text-sm text-[#F2F4F6] focus:border-[#2EE6D6] focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={generating}
                  data-testid="referral-generate-btn"
                  className="w-full rounded-xl bg-gradient-to-r from-[#2EE6D6] to-[#7C5CFF] py-2.5 text-sm font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {generating ? "Generando…" : "Crear mi código"}
                </button>
              </form>
            ) : (
              <div className="mt-4 space-y-3" data-testid="referral-code-result">
                <div className="rounded-xl border border-[#2EE6D6]/40 bg-[#2EE6D6]/10 p-4 text-center">
                  <p className="text-xs uppercase tracking-widest text-[#8A8F98]">Tu código</p>
                  <p className="mt-1 font-mono text-2xl font-extrabold text-[#2EE6D6]">
                    {generated.code}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyCode}
                    data-testid="referral-copy-btn"
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#2D323E] py-2.5 text-sm font-semibold text-[#F2F4F6] transition-colors hover:border-[#2EE6D6]/50 hover:text-[#2EE6D6]"
                  >
                    <Copy className="h-4 w-4" /> Copiar
                  </button>
                  <a
                    href={waLink(shareMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="referral-share-btn"
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#25D366] py-2.5 text-sm font-bold text-black transition-colors hover:bg-[#20BA5A]"
                  >
                    <WhatsAppIcon className="h-4 w-4" /> Compartir
                  </a>
                </div>
                <p className="text-xs leading-relaxed text-[#8A8F98]">
                  Compártelo por WhatsApp o redes. Cada compra con tu código suma un 10% para ti:
                  con 10 amigos, 100% de descuento (hasta 100€).
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-[#2D323E] bg-[#1E2128] p-6">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
            <Search className="h-4 w-4 text-[#2EE6D6]" /> Consulta tus usos acumulados
          </h3>
          <form onSubmit={check} className="mt-3 flex gap-2" data-testid="referral-check-form">
            <input
              type="text"
              value={checkCode}
              onChange={(e) => setCheckCode(e.target.value)}
              data-testid="referral-check-input"
              placeholder="Escribe tu código"
              className="flex-1 rounded-lg border border-[#2D323E] bg-[#16181D] px-3 py-2.5 text-sm text-[#F2F4F6] focus:border-[#2EE6D6] focus:outline-none"
            />
            <button
              type="submit"
              disabled={checking}
              data-testid="referral-check-btn"
              className="rounded-xl border border-[#2EE6D6]/50 px-5 py-2.5 text-sm font-bold text-[#2EE6D6] transition-colors hover:bg-[#2EE6D6] hover:text-black disabled:opacity-50"
            >
              {checking ? "…" : "Consultar"}
            </button>
          </form>
          {checkResult && (
            <p data-testid="referral-check-result" className="mt-3 rounded-lg bg-[#16181D] px-4 py-3 text-sm text-[#F2F4F6]">
              Código <span className="font-mono font-bold text-[#2EE6D6]">{checkResult.code}</span> de{" "}
              {checkResult.owner_name}: <span className="font-bold">{checkResult.uses}</span>{" "}
              {checkResult.uses === 1 ? "uso" : "usos"} → has acumulado un{" "}
              <span className="font-bold text-gradient">
                {Math.min(checkResult.uses * (checkResult.reward_pct ?? 10), 100)}%
              </span>{" "}
              de descuento (tope 100€).
              {checkResult.uses * (checkResult.reward_pct ?? 10) >= 100
                ? " ¡Tienes un pedido de hasta 100€ GRATIS! Menciona tu código por WhatsApp al comprar."
                : " Menciona tu código por WhatsApp al comprar y te lo descontamos del precio final."}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
